import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { ReactNode, useMemo } from 'react';
import { GetStarterGuideOrganisationSteps } from 'shared/data/myAccount/v2';

import { ConnectAccountingSystem } from '../ConnectAccountingSystem/ConnectAccountingSystem';
import { MobileAppDownload } from '../MobileAppDownload/MobileAppDownload';
import { RequestApproval } from '../RequestApproval/RequestApproval';
import { RequestCreation } from '../RequestCreation/RequestCreation';
import { SubscriptionPlan } from '../SubscriptionPlan/SubscriptionPlan';
import { TeamInvitation } from '../TeamInvitation/TeamInvitation';
import { WorkflowSetup } from '../WorkflowSetup/WorkflowSetup';
import imgSrcStep3 from './assets/thumbnailStep3.png';
import imgSrcStep4 from './assets/thumbnailStep4.png';
import imgSrcStep7 from './assets/thumbnailStep7.png';
import { stepsOrdered } from './GettingStarted.constants';
import { getVideoLinks } from './GettingStarted.helpers';
import { GuideStep, UseGettingStartedStepsParams } from './GettingStarted.types';

export const useGettingStartedSteps = (params: UseGettingStartedStepsParams) => {
    const { companyId, integrationType, useCase, steps } = params;

    const profile = useSelector(selectors.profile.findProfile);
    const videoLinks = useMemo(() => getVideoLinks(integrationType), [integrationType]);

    const result = useMemo(() => {
        const stepsContent: ReactNode[] = [];
        const stepsMap = steps.reduce(
            (acc, step) => {
                acc[step.step] = step;

                return acc;
            },
            {} as Record<string, GetStarterGuideOrganisationSteps['response']['steps'][number] | undefined>
        );

        const lastFinishedStep = stepsOrdered.findLast((step) => stepsMap[step]?.isFinished);

        let isStandalone = false;
        let nextStepOpened = lastFinishedStep ? false : true;
        let number = 1;
        let completedSteps = 0;

        stepsOrdered.forEach((step) => {
            if (!stepsMap[step]) {
                return;
            }

            switch (step) {
                case GuideStep.TeamInvitation:
                    stepsContent.push(
                        <TeamInvitation
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                        />
                    );
                    break;

                case GuideStep.WorkflowSetup:
                    stepsContent.push(
                        <WorkflowSetup
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                            imgSrc={imgSrcStep3}
                            videoSrc={videoLinks.step3}
                        />
                    );
                    break;

                case GuideStep.WorkflowSetupStandalone: {
                    stepsContent.push(
                        <WorkflowSetup
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                            imgSrc={imgSrcStep3}
                            videoSrc={videoLinks.step3}
                            isStandalone
                        />
                    );
                    isStandalone = true;
                    break;
                }

                case GuideStep.RequestCreation:
                    stepsContent.push(
                        <RequestCreation
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                            imgSrc={imgSrcStep4}
                            videoSrc={videoLinks.step4}
                        />
                    );
                    break;

                case GuideStep.RequestApproval:
                    stepsContent.push(
                        <RequestApproval
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                        />
                    );
                    break;

                case GuideStep.ConnectAccountingSystem:
                    stepsContent.push(
                        <ConnectAccountingSystem
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                        />
                    );
                    break;

                case GuideStep.MobileAppDownload:
                    stepsContent.push(
                        <MobileAppDownload
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                        />
                    );
                    break;

                case GuideStep.SubscriptionPlan:
                    stepsContent.push(
                        <SubscriptionPlan
                            key={number}
                            number={number++}
                            collapsed={!nextStepOpened}
                            complete={stepsMap[step].isFinished}
                            companyId={companyId}
                            profileId={profile?.id}
                            useCase={useCase}
                            imgSrc={imgSrcStep7}
                            videoSrc={videoLinks.step7}
                        />
                    );
                    break;
            }

            nextStepOpened = lastFinishedStep === step;

            if (stepsMap[step].isFinished) {
                completedSteps++;
            }
        });

        return {
            stepsContent,
            isStandalone,
            completedSteps,
        };
    }, [companyId, profile?.id, steps, useCase, videoLinks.step3, videoLinks.step4, videoLinks.step7]);

    return result;
};
